import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTypedSelector } from 'state';

import styles from './ModerationFilters.module.scss';
import {
  ModelConfigEditable,
  ModelConfigs,
  ModelConfigsEditable,
  ModelConfigsValues,
  ModelConfigValue,
  MultipleThresholds
} from './moderationFiltersTypes';
import { ModerationFiltersThresholds } from './components/ModerationFiltersThresholds';
import { getDefaultThresholds } from './moderationFiltersUtils';
import { useDispatch } from 'react-redux';
import { saveModerationFiltersConfigs } from './moderationFilterActions';
import { AnalyticsProvider } from '../../../../../services/analytics-provider';
import { ConfirmationModalProps } from '../../../../moderation-v2/sharable/components/modals/ConfirmationModal';
import { createTwoButtonsRenderActions } from '../../../../moderation-v2/sharable/components/modals/ConfirmationModal/confirmationModalUtils';
import { openModal } from '../../../../moderation-v2/comments-and-posts/modals/state/actions';
import { ModalType } from '../../../../moderation-v2/comments-and-posts/modals';
import { ModerationFiltersModels } from './components/ModerationFiltersModels';
import { useSendEvent } from '../../../../../tracking/useSendEvent';
import classNames from 'classnames';

interface ModerationFiltersProps {
  isSuperAdmin: boolean;
  spotId: string;
  isAIModerationAssistanceEnabled: boolean;
  handleAIModerationAssistance: (isEnabled: boolean) => void;
}

export function ModerationFilters(props: ModerationFiltersProps) {
  const {
    isSuperAdmin,
    spotId,
    isAIModerationAssistanceEnabled,
    handleAIModerationAssistance
  } = props;
  const { isFetched, models } = useTypedSelector(state => state.settings.policy.moderationFilters);

  const [currentModel, setCurrentModel] = useState<string>();
  const [modelConfigs, setModelConfigs] = useState<ModelConfigsEditable>({});
  const [dataChanged, setDataChanged] = useState(false);

  const selectionsChanged = useRef(false);
  const modelsWithNewThresholds = useRef<Set<string>>(new Set());

  const dispatch = useDispatch();
  const { sendEvent } = useSendEvent();

  const hasActiveModels = useMemo(() => {
    return Object.values(modelConfigs).some(config => config.selected);
  }, [modelConfigs]);

  const onCurrentModelChange = useCallback((model: string) => {
    setCurrentModel(model);
  }, []);

  const onSelectionChange = (model: string) => {
    selectionsChanged.current = true;
    updateModelConfigs(model, { selected: !modelConfigs[model].selected });
  };

  const onConfigChange = (model: string, value: ModelConfigValue) => {
    modelsWithNewThresholds.current.add(model);
    updateModelConfigs(model, { value });
  };

  const updateModelConfigs = (model: string, update: Partial<ModelConfigEditable>) => {
    const config = modelConfigs[model];
    const modifiedConfig = {
      ...config,
      ...update
    };

    const newModelConfigs = {
      ...modelConfigs,
      [model]: modifiedConfig
    };

    setModelConfigs(newModelConfigs);
    setDataChanged(true);
  };

  const handleSave = () => {
    AnalyticsProvider.track('Perspective save button clicked');

    const configs = convertEditableToModelConfigs(modelConfigs);
    dispatch(
      saveModerationFiltersConfigs({
        spotId,
        configs,
        onResult: be_success => {
          if (be_success) {
            setDataChanged(false);
          }

          if (selectionsChanged.current) {
            selectionsChanged.current = false;

            const names = getEnabledModelsNames(modelConfigs);

            sendEvent({
              type: 'Auto_mod_filter_changed',
              element: { enabled_model_names: names },
              be_success
            });
          }

          if (modelsWithNewThresholds.current.size > 0) {
            const newThresholdsElements: Array<ThresholdElement> = [];

            for (const model of modelsWithNewThresholds.current) {
              const elements = getNewThresholdsElements(model, modelConfigs, models);
              newThresholdsElements.push(...elements);
            }

            newThresholdsElements.forEach(element => {
              sendEvent({
                type: 'Auto_mod_threshold_changed',
                element,
                be_success
              });
            });

            modelsWithNewThresholds.current.clear();
          }
        }
      })
    );
  };

  const onSaveClick = () => {
    const modalProps: ConfirmationModalProps = {
      title: 'Confirm Action',
      description:
        'Are you sure you want to save these changes? These changes may impact the amount of comments being sent to the pending queue.',
      renderActions: createTwoButtonsRenderActions({ primaryButtonOnClick: handleSave })
    };

    dispatch(
      openModal({
        modalType: ModalType.CONFIRM_GENERAL,
        modalProps
      })
    );
  };

  const onSaveWithAIEnabled = () => {
    const modalProps: ConfirmationModalProps = {
      title: 'Disable All Automated Moderation Filters',
      description:
        'Disabling this feature will also turn off AI Moderation Assist. Both work hand-in-hand for optimal moderation. Are you sure you want to proceed?',
      renderActions: createTwoButtonsRenderActions({
        primaryButtonLabel: 'Disable',
        primaryButtonTheme: 'outlined-apply-dangerous',
        primaryButtonOnClick: () => {
          handleSave();
          handleAIModerationAssistance(false);
        }
      })
    };

    dispatch(
      openModal({
        modalType: ModalType.CONFIRM_GENERAL,
        modalProps
      })
    );
  };

  useEffect(() => {
    if (models) {
      const configs = getEditableConfigs(models);

      setModelConfigs(configs);

      const firstKey = Object.keys(configs)[0];
      setCurrentModel((prevModel: string) => prevModel || firstKey);
    }
  }, [models]);

  if (!isFetched) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.filtersContainer}>
        <ModerationFiltersModels
          currentModel={currentModel}
          configs={modelConfigs}
          onCurrentModelChange={onCurrentModelChange}
          onSelectionChange={onSelectionChange}
        />
        {currentModel && (
          <ModerationFiltersThresholds
            isSuperAdmin={isSuperAdmin}
            config={modelConfigs[currentModel]}
            currentModel={currentModel}
            onConfigChange={onConfigChange}
          />
        )}
      </div>
      <button
        className={classNames('save-button', 'button-green-medium', {
          [styles.disabledButton]: !dataChanged
        })}
        onClick={
          isAIModerationAssistanceEnabled && !hasActiveModels ? onSaveWithAIEnabled : onSaveClick
        }
      >
        Save
      </button>
    </div>
  );
}

function getEditableConfigs(configs: ModelConfigs): ModelConfigsEditable {
  const result: ModelConfigsEditable = {};
  const sortedKeys = getSortedKeys(configs);

  for (const key of sortedKeys) {
    const config = configs[key];
    result[key] = {
      ...config,
      selected: !!config.value,
      value: config.value || getDefaultThresholds(key)
    };
  }

  return result;
}

function getSortedKeys(configs: ModelConfigs) {
  return Object.keys(configs).sort((a, b) => {
    const configA = configs[a];
    const configB = configs[b];

    const labelA = configA.label;
    const labelB = configB.label;

    return labelA.localeCompare(labelB);
  });
}

function convertEditableToModelConfigs(configs: ModelConfigsEditable): ModelConfigsValues {
  const result: ModelConfigsValues = {};

  for (const key in configs) {
    const config = configs[key];
    const { selected, value } = config;
    if (selected) {
      result[key] = value;
    }
  }

  return result;
}

function getEnabledModelsNames(configs: ModelConfigsEditable) {
  const result: string[] = [];

  for (const key in configs) {
    const config = configs[key];
    if (config.selected) {
      result.push(config.label);
    }
  }

  return result;
}

interface ThresholdElement {
  model_group: string;
  threshold_type?: string;
  model_name: string;
  new_threshold: number;
}

function getNewThresholdsElements(
  model: string,
  updatedConfigs: ModelConfigsEditable,
  initConfigs: ModelConfigs
) {
  const result: Array<ThresholdElement> = [];

  const { selected, value } = updatedConfigs[model];
  if (!selected) {
    return result;
  }

  if (value.type === 'single') {
    return [
      { model_group: getModelGroup(model), model_name: model, new_threshold: value.threshold }
    ];
  }

  if (value.type === 'multiple') {
    const { reject, publishAndModerate, requireApproval } = value;

    const initConfig = initConfigs[model];
    const {
      reject: initReject,
      requireApproval: initRequireApproval,
      publishAndModerate: initPublishAndModerate
    } = initConfig.value as MultipleThresholds;

    if (reject !== initReject) {
      result.push(createThresholdElement(model, 'Reject', reject));
    }

    if (requireApproval !== initRequireApproval) {
      result.push(createThresholdElement(model, 'Require Approval', requireApproval));
    }

    if (publishAndModerate !== initPublishAndModerate) {
      result.push(createThresholdElement(model, 'Publish and Moderate', publishAndModerate));
    }
  }

  return result;
}

function getModelGroup(model: string) {
  return model.startsWith('ow_') ? 'OW_Models' : 'Production';
}

function createThresholdElement(
  model: string,
  type: string,
  newThreshold: number
): ThresholdElement {
  return {
    model_group: getModelGroup(model),
    threshold_type: type,
    model_name: model,
    new_threshold: newThreshold
  };
}
